export interface ServiceAreasContent {
  eyebrow: string;
  headline: string;
  description: string;
  areas: string[];
  note: string;
  mapEmbedUrl?: string;
}

interface Props {
  content: ServiceAreasContent;
}

const ServiceAreas = ({ content }: Props) => {
  const { eyebrow, headline, description, areas, note, mapEmbedUrl } = content;

  return (
    <section className="bg-white py-0 overflow-hidden relative">
      {/* Top gold accent */}
      <div className="absolute top-0 left-0 right-0 h-[1px] z-10" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(201,168,76,0.4) 30%, rgba(201,168,76,0.4) 70%, transparent 100%)' }} />

      {/* Full-width map */}
      <div className="relative w-full" style={{ height: '520px' }}>
        <iframe
          src={mapEmbedUrl || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d211476.6!2d-117.4!3d34.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c35aba99c5df1d%3A0xa6dce6dcd42ab8de!2sSan%20Bernardino%2C%20CA!5e0!3m2!1sen!2sus!4v1711000000000!5m2!1sen!2sus"}
          width="100%"
          height="100%"
          style={{ border: 0, filter: 'grayscale(25%) contrast(1.05)' }}
          allowFullScreen
          loading="lazy"
          title="Drain and Sewer Pro service area map"
        />
        {/* Dark overlay at bottom for content overlap */}
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,1))' }} />
      </div>

      {/* Content section */}
      <div className="relative z-10 py-24" style={{ marginTop: '-2px' }}>
        <div className="max-w-[1280px] mx-auto px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-[1px] w-10 bg-gold" />
              <span className="text-gold text-[11px] tracking-[0.3em] uppercase font-sans font-semibold">
                {eyebrow}
              </span>
              <div className="h-[1px] w-10 bg-gold" />
            </div>
            <h2 className="font-serif text-navy font-bold leading-tight mb-5"
              style={{ fontSize: 'clamp(2.2rem, 4vw, 3.5rem)' }}>
              {headline}
            </h2>
            <p className="text-navy/45 text-base max-w-[500px] mx-auto font-sans leading-relaxed">{description}</p>
          </div>

          {/* City grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-12">
            {areas.map((area, idx) => (
              <div
                key={area}
                className="flex flex-col items-center gap-1.5 py-4 px-3 rounded-xl bg-white border border-surface-border hover:border-teal/25 transition-all duration-300 group cursor-default text-center"
                style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 20px rgba(26,74,82,0.10)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.04)')}
              >
                <span className="text-teal/45 text-[10px] font-sans font-semibold tabular-nums group-hover:text-teal/80 transition-colors">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span className="text-charcoal/70 text-[13px] font-sans font-medium leading-snug group-hover:text-charcoal transition-colors">
                  {area}
                </span>
                <div className="w-1 h-1 bg-teal/25 rounded-full group-hover:bg-teal/60 transition-colors" />
              </div>
            ))}
          </div>

          {/* Note + CTA strip */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-surface-border">
            <div className="flex items-start gap-3 max-w-[480px]">
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-teal mt-0.5">
                <i className="ri-information-line text-base" />
              </div>
              <p className="text-charcoal/40 text-[13px] italic font-sans leading-relaxed">{note}</p>
            </div>
            <a
              href="tel:+19090000000"
              className="flex items-center gap-3 bg-teal hover:bg-teal-mid text-white font-semibold text-[12px] px-8 py-4 rounded transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.12em] uppercase group"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              Call for Service in Your Area
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
